import { useActiveVkuiLocation, useBlocker, type Blocker } from '@vkontakte/vk-mini-apps-router';
import { FormItem, Input, Textarea, type NavIdProps } from '@vkontakte/vkui';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cabinetApi, type FormVersionRow, type SaveFormBody } from '../../api/cabinet';
import { errorMessage } from '../../api/client';
import {
  FORM_ABILITIES_MAX,
  FORM_FIELD_LABELS,
  REVIEW_STATUS_LABELS,
  type FormAbilityEntry,
  type ReviewStatus,
} from '../../api/types';
import { holdLeave, leaveGuarded } from '../../app/leaveGuard';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import {
  Action,
  ActionRow,
  ErrorState,
  FieldHint,
  FieldLabel,
  FieldPair,
  FormEntry,
  FormEntryAdd,
  FormEntryList,
  FormFooter,
  FormFooterSpacer,
  Gutter,
  ImageField,
  LoadingState,
  Prose,
  Screen,
  Section,
  SectionTitle,
  StatusBadge,
  toast,
  type StatusTone,
} from '../../shared/ui';
import { Dialog } from '../review/staffKit';

/**
 * Редактор анкеты.
 *
 * ЧЕРНОВИК НЕ ХРАНИТСЯ В LOCALSTORAGE — И НЕ ДОЛЖЕН.
 * Хостинг мини-приложений ВК выдаёт новый домен на каждый деплой, а вместе с
 * доменом теряется всё браузерное хранилище: localStorage, sessionStorage,
 * IndexedDB. Игрок, просидевший вечер над биографией, после ближайшего
 * обновления приложения открыл бы пустую анкету и никогда не узнал почему.
 * Единственное место, где живёт черновик, — сервер.
 *
 * Сохранение ЯВНОЕ: по кнопке и на выходе. Автосохранения по паузе в наборе
 * здесь больше нет — из-за него черновик заводился от одного лишь захода в
 * редактор: поля засеиваются принятой версией, и уход с экрана отправлял их
 * целиком. Признак «менялось ли что-нибудь» считается от seedRef — того, что
 * показали при открытии, — а не от savedRef, который у ещё не созданной
 * версии пуст и потому делает любой черновик «изменённым».
 *
 * Разделы собраны по смыслу, а не по номерам пунктов анкеты: одиннадцать
 * блоков, в шести из которых лежало по одному полю, читались как нарезка, а не
 * как группировка. Номера из подписей ушли вместе с ней — группа теперь
 * покрывает несмежные пункты, и её номер был бы враньём.
 *
 * Валидации здесь нет намеренно: заказчик отказался от обязательных полей и
 * минимальных длин. Единственная оставшаяся проверка — ФИО на русском при
 * отправке и при сохранении правки заявки, которая уже в очереди; у черновика
 * её нет. Здесь она стоит кнопками: без имени не нажать ни «отправить», ни
 * «сохранить правку». Запереть игрока в редакторе это не может — выход
 * спрашивает, и уйти без сохранения можно всегда.
 */

type TextKey =
  | 'nameRu'
  | 'nameEn'
  | 'nicknames'
  | 'gender'
  | 'age'
  | 'familyText'
  | 'race'
  | 'appearanceText'
  | 'personalityText'
  | 'biographyText'
  | 'molesCount';

const TEXT_KEYS: readonly TextKey[] = [
  'nameRu',
  'nameEn',
  'nicknames',
  'gender',
  'age',
  'familyText',
  'race',
  'appearanceText',
  'personalityText',
  'biographyText',
  'molesCount',
];

/** Зеркало @MaxLength из SaveFormDto: длиннее сервер не примет, и лучше не дать набрать. */
const TEXT_MAX: Record<TextKey, number> = {
  nameRu: 200,
  nameEn: 200,
  nicknames: 700,
  gender: 100,
  age: 100,
  familyText: 5000,
  race: 200,
  appearanceText: 10000,
  personalityText: 10000,
  biographyText: 30000,
  molesCount: 200,
};

const ABILITY_TITLE_MAX = 200;
const ABILITY_DESCRIPTION_MAX = 4000;

type Draft = Record<TextKey, string> & {
  appearanceImageId: string | null;
  abilities: FormAbilityEntry[];
};

type SaveState = 'clean' | 'pending' | 'saving' | 'saved' | 'error';

/** Открытая версия бывает только черновиком или заявкой — остальное для полноты. */
const OPEN_TONE: Record<ReviewStatus, StatusTone> = {
  draft: 'archived',
  pending: 'pending',
  approved: 'published',
  rejected: 'rejected',
};

const EMPTY_DRAFT: Draft = {
  nameRu: '',
  nameEn: '',
  nicknames: '',
  gender: '',
  age: '',
  familyText: '',
  race: '',
  appearanceText: '',
  personalityText: '',
  biographyText: '',
  molesCount: '',
  appearanceImageId: null,
  abilities: [],
};

export function FormEditorPanel({ id }: NavIdProps) {
  const back = useBack();
  const { panel } = useActiveVkuiLocation();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [loadFailure, setLoadFailure] = useState<{ attempt: number; message: string } | null>(null);
  const loadError = loadFailure?.attempt === attempt ? loadFailure.message : null;

  /** Статус открытой версии на сервере. null — открытой версии ещё нет. */
  const [openStatus, setOpenStatus] = useState<ReviewStatus | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('clean');
  const [submitting, setSubmitting] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leavingBusy, setLeavingBusy] = useState(false);
  /** Почему не удалось сохранить на выходе. Не null — диалог предлагает выйти, потеряв правки. */
  const [leaveError, setLeaveError] = useState<string | null>(null);

  /** Без ФИО на русском анкету не отправить, а заявку в очереди — не сохранить. */
  const nameMissing = (draft?.nameRu ?? '').trim() === '';

  const unsaved = saveState === 'pending' || saveState === 'saving' || saveState === 'error';
  const canSubmit = unsaved || openStatus === 'draft';
  const showFooter = openStatus === 'pending' ? unsaved : canSubmit;

  const draftRef = useRef<Draft | null>(null);
  /** Снимок, который заведомо лежит на сервере. null значит «открытой версии нет». */
  const savedRef = useRef<Draft | null>(null);
  /** Что показали при открытии. От него, а не от savedRef, считается «изменилось». */
  const seedRef = useRef<Draft | null>(null);
  const savingRef = useRef(false);
  /** Вопрос уже задан и уход разрешён: блокировщик роутера не спрашивает второй раз. */
  const allowLeaveRef = useRef(false);
  /** Экран открыт. Ответ отправки, пришедший после ухода, уводить никуда не должен. */
  const openRef = useRef(false);
  /**
   * Отдаёт текст ошибки или null. Ошибку глотает сам — индикатор наверху её
   * покажет, — но уходить с экрана и отправлять на проверку по ней нельзя, а
   * тому, кто нажал кнопку внизу анкеты, индикатор не виден.
   */
  const saveNowRef = useRef<() => Promise<string | null>>(async () => null);

  useLayoutEffect(() => {
    saveNowRef.current = async () => {
      const sent = draftRef.current;
      if (sent === null) return null;

      // Запрос уже в пути: второй обгонит первый, и на сервере окажется
      // более старый текст. Дожидаться нечего — кнопку нажмут ещё раз.
      if (savingRef.current) return t('character.editor.error.saving');

      // База сравнения — открытая версия, а если её ещё нет, то показанная при
      // открытии: сервер заведёт новую версию от неё же и доберёт неприсланное.
      const patch = buildPatch(savedRef.current ?? seedRef.current, sent);
      if (patch === null) {
        setSaveState((prev) => (prev === 'error' ? prev : 'saved'));
        return null;
      }

      savingRef.current = true;
      setSaveState('saving');
      try {
        const row = await cabinetApi.saveForm(patch);
        // Запоминаем именно отправленный снимок, а не ответ сервера: сервер
        // подрезает пробелы, и сравнение с ответом гоняло бы одно и то же поле.
        savedRef.current = sent;
        seedRef.current = sent;
        setOpenStatus(row.status);
        setSaveState(draftRef.current === sent ? 'saved' : 'pending');
        return null;
      } catch (cause) {
        const message = errorMessage(cause);
        setSaveState('error');
        return message;
      } finally {
        savingRef.current = false;
      }
    };
  });

  useEffect(() => {
    let alive = true;

    void (async () => {
      try {
        // Какую версию показать, решает сервер: открытую, иначе принятую, иначе
        // завёрнутую. Историю ради этого больше не выкачиваем.
        const mine = await cabinetApi.form();
        if (!alive) return;
        const onServer = mine.source === 'open';
        const seed = mine.version === null ? EMPTY_DRAFT : toDraft(mine.version);

        draftRef.current = seed;
        savedRef.current = onServer ? seed : null;
        seedRef.current = seed;
        setDraft(seed);
        setOpenStatus(onServer && mine.version !== null ? mine.version.status : null);
        setSaveState('clean');
      } catch (cause: unknown) {
        if (alive) setLoadFailure({ attempt, message: errorMessage(cause) });
      }
    })();

    return () => {
      alive = false;
    };
  }, [attempt]);

  const patchDraft = useCallback((patch: Partial<Draft>) => {
    const prev = draftRef.current;
    if (prev === null) return;
    const next = { ...prev, ...patch };
    draftRef.current = next;
    // Разрешённый уход не состоялся, раз правят дальше: сторож снова нужен.
    allowLeaveRef.current = false;
    setDraft(next);
    setSaveState('pending');
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Сначала дописываем несохранённое: иначе модератор получит анкету
      // без последнего абзаца, который игрок как раз и дописывал.
      const failed = await saveNowRef.current();
      if (failed !== null) {
        toast(failed);
        return;
      }
      const row = await cabinetApi.submitForm();
      setOpenStatus(row.status);
      // Шагом назад, а не push: иначе системный «назад» из хаба вернул бы в редактор.
      // Ушёл, не дождавшись ответа, — второй шаг назад увёл бы уже с чужого экрана.
      if (openRef.current) back();
    } catch (cause) {
      toast(errorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  /** Менялось ли хоть что-то с момента открытия. Считается от seedRef. */
  const isDirty = useCallback(
    () =>
      draftRef.current !== null &&
      seedRef.current !== null &&
      buildPatch(seedRef.current, draftRef.current) !== null,
    [],
  );

  /*
    Системный «назад» (кнопка Android в клиенте ВК, «назад» браузера) шагает по
    истории мимо leaveGuard, поэтому его останавливает роутер. Тот же блокировщик
    видит и уходы, уже прошедшие через вопрос, — их пропускает allowLeaveRef.
  */
  const blockLeave = useCallback(() => !allowLeaveRef.current && isDirty(), [isDirty]);
  const blocker = useBlocker(blockLeave);

  /*
    Состояние блокировщика роутер отдаёт с опозданием до 400 мс, и отвеченная
    остановка всё это время выглядит открытой: окно не гасло бы, а второй
    proceed() на ней бросил бы «Invalid blocker state transition». Каждая
    остановка — новый объект, поэтому отвеченную узнаём по ссылке.
  */
  const [answered, setAnswered] = useState<Blocker | null>(null);
  const stopped = blocker.state === 'blocked' && blocker !== answered ? blocker : null;

  /** Куда уходит игрок, когда вопрос закрыт. Ставит его тот, кто уход начал. */
  const pendingRef = useRef<(() => void) | null>(null);

  const asking = leaving || stopped !== null;

  const leave = () => {
    setLeaving(false);
    setLeaveError(null);
    if (stopped !== null) setAnswered(stopped);

    // Уход уже отпущен, и новых остановок блокировщик больше не делает —
    // значит, та, что на экране, устарела.
    const proceed =
      pendingRef.current ?? (allowLeaveRef.current ? null : (stopped?.proceed ?? null));
    pendingRef.current = null;
    if (proceed === null) return;

    allowLeaveRef.current = true;
    proceed();
  };

  const stay = () => {
    setLeaving(false);
    setLeaveError(null);
    pendingRef.current = null;
    if (stopped === null) return;

    setAnswered(stopped);
    stopped.reset();
  };

  const goBack = () => leaveGuarded(back);

  /*
    С несохранённым текстом из редактора молча не выпускаем и молча не
    сохраняем: спрашиваем. Раньше правка заявки, стоящей на проверке,
    сохранялась сама — выход из редактора оказывался действием, которого
    игрок не совершал, и отменить его было уже нельзя.

    Сторож стоит на ЛЮБОМ уходе, а не только на своей кнопке возврата: снизу
    висит таббар, и уйти из анкеты можно им. Снимаем его, как только экран
    перестал быть открытым: уходящая панель живёт ещё весь переход, и сторож
    с неё держал бы уже новый экран.
  */
  useEffect(() => {
    if (id === undefined || panel !== id) return;
    openRef.current = true;

    holdLeave((proceed) => {
      if (!isDirty()) {
        proceed();
        return;
      }

      pendingRef.current = proceed;
      setLeaving(true);
    });

    return () => {
      openRef.current = false;
      holdLeave(null);
    };
  });

  /*
    Строки состояния на экране больше нет: «всё сохранено» ничего не сообщало,
    а «не сохранилось» висело плашкой посреди анкеты. О неудаче говорит
    снекбар — там же, где о ней говорят остальные экраны.
  */
  const saveNow = async () => {
    const failed = await saveNowRef.current();
    if (failed !== null) toast(failed);
  };

  const saveAndLeave = async () => {
    setLeavingBusy(true);
    try {
      const failed = await saveNowRef.current();
      if (failed === null) leave();
      else setLeaveError(failed);
    } finally {
      setLeavingBusy(false);
    }
  };

  /*
    Состояние версии — в правом краю шапки, как в макете. Экран отдаёт его
    пропом `after`, а не рисует в шапке сам: одноимённый слот у вкуишного
    PanelHeader при кнопках ВК выбрасывается вместе с содержимым.
  */
  return (
    <Screen
      id={id}
      title={t('common.section.form')}
      back={goBack}
      after={
        openStatus === null ? undefined : (
          <StatusBadge tone={OPEN_TONE[openStatus]}>
            {REVIEW_STATUS_LABELS[openStatus].toLocaleLowerCase('ru')}
          </StatusBadge>
        )
      }
    >
      {draft === null && loadError === null ? <LoadingState /> : null}

      {loadError !== null ? (
        <ErrorState
          title={t('character.editor.error.loadTitle')}
          message={loadError}
          onRetry={() => setAttempt((value) => value + 1)}
        />
      ) : null}

      {draft !== null ? (
        <>
          <Section>
            <SectionTitle>{t('character.editor.section.names')}</SectionTitle>
            <FormItem top={<FieldLabel required>{FORM_FIELD_LABELS.nameRu}</FieldLabel>}>
              <Input
                value={draft.nameRu}
                placeholder={t('character.editor.placeholder.nameRu')}
                maxLength={TEXT_MAX.nameRu}
                onChange={(event) => patchDraft({ nameRu: event.target.value })}
              />
            </FormItem>
            <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.nameEn}</FieldLabel>}>
              <Input
                value={draft.nameEn}
                maxLength={TEXT_MAX.nameEn}
                onChange={(event) => patchDraft({ nameEn: event.target.value })}
              />
            </FormItem>
            <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.nicknames}</FieldLabel>}>
              <Input
                value={draft.nicknames}
                placeholder={t('character.editor.placeholder.nicknames')}
                maxLength={TEXT_MAX.nicknames}
                onChange={(event) => patchDraft({ nicknames: event.target.value })}
              />
            </FormItem>
          </Section>

          <Section>
            <SectionTitle>{t('character.editor.section.appearance')}</SectionTitle>
            <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.appearanceText}</FieldLabel>}>
              <Textarea
                rows={3}
                value={draft.appearanceText}
                maxLength={TEXT_MAX.appearanceText}
                onChange={(event) => patchDraft({ appearanceText: event.target.value })}
              />
            </FormItem>
            <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.appearanceImageId}</FieldLabel>}>
              <ImageField
                label={FORM_FIELD_LABELS.appearanceImageId}
                imageId={draft.appearanceImageId}
                hint={t('character.editor.hint.appearanceImage')}
                onChange={(appearanceImageId) => patchDraft({ appearanceImageId })}
              />
            </FormItem>
          </Section>

          <Section>
            <SectionTitle accent="pink">{t('character.editor.section.personality')}</SectionTitle>
            <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.personalityText}</FieldLabel>}>
              <Textarea
                rows={3}
                value={draft.personalityText}
                maxLength={TEXT_MAX.personalityText}
                onChange={(event) => patchDraft({ personalityText: event.target.value })}
              />
            </FormItem>
            <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.biographyText}</FieldLabel>}>
              <Textarea
                rows={3}
                value={draft.biographyText}
                maxLength={TEXT_MAX.biographyText}
                onChange={(event) => patchDraft({ biographyText: event.target.value })}
              />
            </FormItem>
          </Section>

          <Section>
            <SectionTitle>{t('character.editor.section.family')}</SectionTitle>
            <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.familyText}</FieldLabel>}>
              <Textarea
                rows={3}
                value={draft.familyText}
                maxLength={TEXT_MAX.familyText}
                onChange={(event) => patchDraft({ familyText: event.target.value })}
              />
            </FormItem>
          </Section>

          <AbilitiesEditor
            entries={draft.abilities}
            onChange={(abilities) => patchDraft({ abilities })}
          />

          <Section>
            <SectionTitle>{t('character.editor.section.misc')}</SectionTitle>
            {/* Пол и возраст — по паре слов каждый: порознь они раздували
                анкету двумя полосами во всю ширину. */}
            <FieldPair>
              <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.gender}</FieldLabel>}>
                <Input
                  value={draft.gender}
                  maxLength={TEXT_MAX.gender}
                  onChange={(event) => patchDraft({ gender: event.target.value })}
                />
              </FormItem>
              <FormItem
                top={
                  <FieldLabel hint={t('character.editor.hint.age')}>
                    {FORM_FIELD_LABELS.age}
                  </FieldLabel>
                }
              >
                <Input
                  value={draft.age}
                  maxLength={TEXT_MAX.age}
                  onChange={(event) => patchDraft({ age: event.target.value })}
                />
              </FormItem>
            </FieldPair>
            <FormItem
              top={
                <FieldLabel hint={t('character.editor.hint.race')}>
                  {FORM_FIELD_LABELS.race}
                </FieldLabel>
              }
            >
              <Input
                value={draft.race}
                maxLength={TEXT_MAX.race}
                onChange={(event) => patchDraft({ race: event.target.value })}
              />
            </FormItem>
            <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.molesCount}</FieldLabel>}>
              <Input
                value={draft.molesCount}
                maxLength={TEXT_MAX.molesCount}
                onChange={(event) => patchDraft({ molesCount: event.target.value })}
              />
            </FormItem>
          </Section>

          {showFooter ? (
            <>
              <FormFooterSpacer />

              <FormFooter>
                {openStatus === 'pending' ? (
                  <Action
                    tone="accent"
                    size="l"
                    stretched
                    loading={saveState === 'saving'}
                    disabled={saveState === 'saving' || nameMissing}
                    onClick={() => void saveNow()}
                  >
                    {t('character.editor.action.saveEdit')}
                  </Action>
                ) : (
                  <ActionRow>
                    {unsaved ? (
                      <Action
                        tone="accent"
                        size="l"
                        loading={saveState === 'saving'}
                        disabled={saveState === 'saving'}
                        onClick={() => void saveNow()}
                      >
                        {t('common.action.save')}
                      </Action>
                    ) : null}
                    <Action
                      tone="action"
                      size="l"
                      stretched
                      loading={submitting}
                      disabled={submitting || nameMissing}
                      onClick={() => void submit()}
                    >
                      {t('character.action.submit')}
                    </Action>
                  </ActionRow>
                )}
              </FormFooter>
            </>
          ) : null}
        </>
      ) : null}

      {asking ? (
        <Dialog
          title={
            leaveError !== null
              ? t('character.editor.leave.errorTitle')
              : openStatus === 'pending'
                ? t('character.editor.leave.saveEditTitle')
                : t('character.editor.leave.saveDraftTitle')
          }
          onClose={leavingBusy ? () => undefined : stay}
        >
          <Gutter>
            <Prose>
              {leaveError ??
                (openStatus === 'pending'
                  ? t('character.editor.leave.pendingText')
                  : t('character.editor.leave.draftText'))}
            </Prose>
          </Gutter>
          {/* Заявку без ФИО сервер не сохранит, поэтому и предлагать
              нечего: остаётся уйти без сохранения. */}
          <Gutter>
            <Action
              tone="accent"
              size="l"
              stretched
              loading={leavingBusy}
              disabled={leavingBusy || (openStatus === 'pending' && nameMissing)}
              onClick={() => void saveAndLeave()}
            >
              {leaveError !== null
                ? t('common.action.retry')
                : openStatus === 'pending'
                  ? t('character.editor.action.saveEdit')
                  : t('character.editor.action.saveDraft')}
            </Action>
          </Gutter>
          <Gutter>
            <Action tone="danger" size="l" stretched disabled={leavingBusy} onClick={leave}>
              {leaveError === null
                ? t('character.editor.leave.discard')
                : t('character.editor.leave.leaveAnyway')}
            </Action>
          </Gutter>
        </Dialog>
      ) : null}
    </Screen>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Особые способности
   ────────────────────────────────────────────────────────────────────────── */

function AbilitiesEditor({
  entries,
  onChange,
}: {
  entries: FormAbilityEntry[];
  onChange: (next: FormAbilityEntry[]) => void;
}) {
  const patchEntry = (index: number, patch: Partial<FormAbilityEntry>) => {
    onChange(entries.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  };

  // Ключ строки живёт отдельно от текста: с ключом-индексом удалённая из
  // середины запись оставляла свои поля соседке. Список другой длины пришёл
  // снаружи — это загрузка анкеты, ключи заводим заново.
  const [keys, setKeys] = useState(() => entries.map((_, i) => i));
  if (keys.length !== entries.length) setKeys(entries.map((_, i) => i));

  const removeEntry = (index: number) => {
    setKeys(keys.filter((_, i) => i !== index));
    onChange(entries.filter((_, i) => i !== index));
  };

  const addEntry = () => {
    setKeys([...keys, Math.max(-1, ...keys) + 1]);
    onChange([...entries, { title: '', description: '' }]);
  };

  return (
    <Section>
      <SectionTitle>{t('common.section.abilities')}</SectionTitle>

      {/* Два уровня подписи, как в макете: раздел анкеты и само поле. Без
          второго список записей висел прямо под лучом раздела и читался как
          его продолжение, а не как значение поля.

          Обёртка — тот же FormItem, что у остальных тринадцати полей: просвет
          между подписью и значением приходит оттуда же, откуда у них, а не
          распоркой по месту. */}
      <FormItem top={<FieldLabel>{FORM_FIELD_LABELS.abilities}</FieldLabel>}>
        <FormEntryList>
          {entries.map((entry, index) => (
            <FormEntry
              key={keys[index]}
              number={index + 1}
              removeLabel={t('character.editor.action.removeAbility', { n: index + 1 })}
              onRemove={() => removeEntry(index)}
              head={
                <Input
                  value={entry.title}
                  placeholder={t('common.field.title')}
                  maxLength={ABILITY_TITLE_MAX}
                  onChange={(event) => patchEntry(index, { title: event.target.value })}
                />
              }
            >
              <Textarea
                rows={3}
                value={entry.description ?? ''}
                placeholder={t('character.editor.placeholder.abilityDescription')}
                maxLength={ABILITY_DESCRIPTION_MAX}
                onChange={(event) => patchEntry(index, { description: event.target.value })}
              />
            </FormEntry>
          ))}

          {/* Предел выбран — полосы «завести ещё» нет вовсе. Погашенная кнопка
              с объяснением, почему она погашена, занимает место и ничего не
              даёт: список из двух записей и так показывает, что мест нет. */}
          {entries.length >= FORM_ABILITIES_MAX ? null : (
            <FormEntryAdd>
              <Action tone="add" size="m" onClick={addEntry}>
                {t('character.editor.action.addAbility')}
              </Action>
              <FieldHint>
                {t('character.editor.hint.abilitiesUsed', {
                  used: entries.length,
                  max: FORM_ABILITIES_MAX,
                })}
              </FieldHint>
            </FormEntryAdd>
          )}
        </FormEntryList>
      </FormItem>
    </Section>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Черновик ↔ версия анкеты
   ────────────────────────────────────────────────────────────────────────── */

function toDraft(version: FormVersionRow): Draft {
  return {
    nameRu: version.nameRu ?? '',
    nameEn: version.nameEn ?? '',
    nicknames: version.nicknames ?? '',
    gender: version.gender ?? '',
    age: version.age ?? '',
    familyText: version.familyText ?? '',
    race: version.race ?? '',
    appearanceText: version.appearanceText ?? '',
    personalityText: version.personalityText ?? '',
    biographyText: version.biographyText ?? '',
    molesCount: version.molesCount ?? '',
    appearanceImageId: version.appearanceImageId,
    abilities: (version.abilities ?? []).map((entry) => ({
      title: entry.title,
      description: entry.description,
    })),
  };
}

/**
 * Что отправлять — только изменившееся относительно базы. Неприсланное сервер
 * возьмёт из той же базы, поэтому нетронутая картинка внешности в тело не
 * попадает: после передачи персонажа она принадлежит прежнему владельцу, и
 * сервер завернул бы её как чужую.
 */
function buildPatch(saved: Draft | null, current: Draft): SaveFormBody | null {
  const patch: SaveFormBody = {};
  let changed = false;

  for (const key of TEXT_KEYS) {
    if (saved !== null && saved[key] === current[key]) continue;
    patch[key] = current[key];
    changed = true;
  }

  if (saved === null || saved.appearanceImageId !== current.appearanceImageId) {
    patch.appearanceImageId = current.appearanceImageId;
    changed = true;
  }

  if (saved === null || !sameAbilities(saved.abilities, current.abilities)) {
    // Только title и description: ValidationPipe стоит с forbidNonWhitelisted,
    // любое лишнее поле развернёт весь запрос.
    patch.abilities = current.abilities.map((entry) => ({
      title: entry.title,
      description: entry.description,
    }));
    changed = true;
  }

  return changed ? patch : null;
}

function sameAbilities(left: FormAbilityEntry[], right: FormAbilityEntry[]): boolean {
  if (left.length !== right.length) return false;
  return left.every(
    (entry, index) =>
      entry.title === right[index].title &&
      (entry.description ?? '') === (right[index].description ?? ''),
  );
}
